import { useTranslation } from 'react-i18next';
import { Mail, Loader2, AlertCircle } from 'lucide-react';

interface ConfirmInvitationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  practitionerName: string;
  practitionerEmail: string;
  isLoading?: boolean;
  /** For "Link account" flow: "Créer un compte" vs "Envoyer invitation" */
  isLinkAccount?: boolean;
  error?: string | null;
}

export function ConfirmInvitationModal({
  isOpen,
  onClose,
  onConfirm,
  practitionerName,
  practitionerEmail,
  isLoading = false,
  isLinkAccount = false,
  error = null,
}: ConfirmInvitationModalProps) {
  const { t } = useTranslation();
  if (!isOpen) return null;

  const title = isLinkAccount ? t('practitionerInvitation.confirmModal.titleLink') : t('practitionerInvitation.confirmModal.titleSend');
  const message = isLinkAccount
    ? t('practitionerInvitation.confirmModal.messageLink')
    : t('practitionerInvitation.confirmModal.messageSend');
  const sendToLabel = isLinkAccount ? t('practitionerInvitation.confirmModal.createAndSendTo') : t('practitionerInvitation.confirmModal.sendTo');
  const confirmLabel = isLinkAccount ? t('practitionerInvitation.confirmModal.createAndSendButton') : t('practitionerInvitation.confirmModal.sendButton');

  return (
    <div className={`fixed inset-0 bg-black/50 flex items-center justify-center z-50 ${isLoading ? 'cursor-wait' : ''}`}>
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 overflow-hidden">
        <div className="bg-gradient-to-br from-primary-500 to-accent-600 px-6 py-5 text-white">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/20">
              <Mail className="h-6 w-6" />
            </div>
            <div>
              <h3 className="text-lg font-semibold">{title}</h3>
              <p className="text-white/90 text-sm mt-0.5">{message}</p>
            </div>
          </div>
        </div>
        <div className="px-6 py-5">
          <p className="text-gray-600 mb-4">
            {sendToLabel}
          </p>
          <div className="rounded-lg bg-gray-50 border border-gray-200 p-4">
            <p className="font-medium text-gray-900">{practitionerName}</p>
            <p className="text-sm text-gray-600 mt-1">{practitionerEmail}</p>
          </div>
          {error && (
            <div className="mt-4 flex items-center gap-2 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>
        <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3 rounded-b-xl">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            {t('practitionerInvitation.confirmModal.cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isLoading}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>{t('practitionerInvitation.confirmModal.sending')}</span>
              </>
            ) : (
              <>
                <Mail className="w-4 h-4" />
                <span>{confirmLabel}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
